import React from 'react';
import { XCircle, FileText, Loader2, Plus } from 'lucide-react';
import type { EcadAutomacaoPasso } from './types';

interface EcadMediaSectionProps {
  passos: EcadAutomacaoPasso[];
  roteiroEditForm: {
    videoUrl: string;
    pdfUrl: string;
  };
  generatingPdf: boolean;
  handleGeneratePdf: (silent?: boolean) => Promise<void>;
}

export const EcadMediaSection: React.FC<EcadMediaSectionProps> = ({
  passos,
  roteiroEditForm,
  generatingPdf,
  handleGeneratePdf
}) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', flexWrap: 'wrap' }}>
        
        {/* Vídeo Homologado */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          borderRadius: '12px',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px'
        }}>
          <h3 style={{ fontSize: '0.9rem', fontWeight: 800, color: '#a78bfa', fontFamily: "'Outfit', sans-serif", margin: 0 }}>
            Vídeo de Simulação (.mp4)
          </h3>

          {roteiroEditForm.videoUrl ? (
            <div style={{ width: '100%', borderRadius: '8px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)', background: '#000' }}>
              <video 
                src={roteiroEditForm.videoUrl} 
                controls 
                style={{ width: '100%', display: 'block', maxHeight: '280px' }} 
              />
            </div>
          ) : (
            <div style={{
              flex: 1,
              minHeight: '160px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px dashed rgba(255,255,255,0.08)',
              borderRadius: '8px',
              color: '#64748b',
              padding: '20px',
              textAlign: 'center'
            }}>
              <XCircle size={28} style={{ color: 'rgba(239, 68, 68, 0.3)', marginBottom: '8px' }} />
              <p style={{ fontSize: '0.8rem', margin: 0 }}>Nenhum vídeo gerado para esta capacitação.</p>
              <span style={{ fontSize: '0.68rem', color: '#475569', marginTop: '4px' }}>Rode o teste homologado na aba de Simulação para gravar.</span>
            </div>
          )}
        </div>

        {/* Manual em PDF */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          borderRadius: '12px',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 800, color: '#a78bfa', fontFamily: "'Outfit', sans-serif", margin: 0 }}>
              Manual de Instruções (PDF)
            </h3>

            <button
              onClick={() => handleGeneratePdf(false)}
              disabled={generatingPdf || passos.length === 0}
              style={{
                background: 'rgba(139, 92, 246, 0.1)',
                border: '1px solid rgba(139, 92, 246, 0.25)',
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '0.75rem',
                color: '#c084fc',
                fontWeight: 700,
                cursor: generatingPdf ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              {generatingPdf ? <Loader2 size={12} className="spin" /> : <Plus size={12} />}
              {roteiroEditForm.pdfUrl ? 'Regerar Manual PDF' : 'Gerar PDF'}
            </button>
          </div>

          {roteiroEditForm.pdfUrl ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
              <div style={{ flex: 1, minHeight: '160px', borderRadius: '8px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
                <iframe 
                  id="ecad-pdf-viewer"
                  name="ecad-pdf-viewer"
                  src={roteiroEditForm.pdfUrl} 
                  style={{ width: '100%', height: '100%', minHeight: '220px', border: 'none', background: '#fff' }} 
                  title="ECAD — Manual PDF Viewer"
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <a 
                  href={roteiroEditForm.pdfUrl} 
                  target="_blank" 
                  rel="noreferrer"
                  style={{
                    fontSize: '0.78rem',
                    color: '#3b82f6',
                    fontWeight: 700,
                    textDecoration: 'none'
                  }}
                >
                  Abrir em nova guia →
                </a>
              </div>
            </div>
          ) : (
            <div style={{
              flex: 1,
              minHeight: '160px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px dashed rgba(255,255,255,0.08)',
              borderRadius: '8px',
              color: '#64748b',
              padding: '20px',
              textAlign: 'center'
            }}>
              <FileText size={28} style={{ color: 'rgba(148, 163, 184, 0.2)', marginBottom: '8px' }} />
              <p style={{ fontSize: '0.8rem', margin: 0 }}>Nenhum manual PDF gerado ainda.</p>
              <span style={{ fontSize: '0.68rem', color: '#475569', marginTop: '4px' }}>Dispare a geração acima para construir o manual dinâmico.</span>
            </div>
          )}
        </div>

      </div>

      {/* Narrações em Áudio por Passo */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid rgba(255, 255, 255, 0.06)',
        borderRadius: '12px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px'
      }}>
        <h3 style={{ fontSize: '0.9rem', fontWeight: 800, color: '#a78bfa', fontFamily: "'Outfit', sans-serif", margin: 0 }}>
          Narrações de Voz da Alice por Passo
        </h3>

        {passos.length === 0 ? (
          <p style={{ fontSize: '0.8rem', color: '#64748b', margin: 0 }}>Nenhum passo cadastrado.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {passos.map((passo, pIdx) => (
              <div 
                key={pIdx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.03)',
                  background: 'rgba(2, 6, 23, 0.3)'
                }}
              >
                <span style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  background: 'rgba(139, 92, 246, 0.1)',
                  border: '1px solid rgba(139, 92, 246, 0.2)',
                  color: '#a78bfa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.72rem',
                  fontWeight: 800
                }}>
                  {pIdx + 1}
                </span>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: '0.68rem', color: '#8b5cf6', fontWeight: 700, textTransform: 'uppercase', display: 'block', marginBottom: '2px' }}>
                    {passo.tipoAcao} {passo.selectorCss ? '(' + passo.selectorCss + ')' : ''}
                  </span>
                  <p style={{ fontSize: '0.78rem', color: '#cbd5e1', margin: 0, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                    {passo.legenda || <span style={{ color: '#475569', fontStyle: 'italic' }}>Sem narração/legenda</span>}
                  </p>
                </div>

                <div>
                  {passo.audioUrl ? (
                    <audio 
                      src={passo.audioUrl} 
                      controls 
                      style={{ height: '30px', width: '240px', display: 'block' }} 
                    />
                  ) : (
                    <span style={{ fontSize: '0.7rem', color: '#64748b', fontStyle: 'italic' }}>
                      Voz não gerada
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
